import { Flex } from '@chakra-ui/layout'
import type { MutableRefObject } from 'react'
import React, { useCallback, useContext, useMemo, useRef } from 'react'
import { isEqual } from 'lodash'
import { useHistory, useLocation } from 'react-router-dom'
import * as History from 'history'
import styled from 'styled-components'
import { Base64 } from 'js-base64'
import { Helmet } from 'react-helmet-async'
import { capitalize } from 'lodash'
import { useTranslation } from 'react-i18next'
import {
  MLHeaderActionbar,
  MLHeaderToolbar,
} from '../../components/MediaLibrary'
import { ContextMenu } from '../../components/MediaLibrary/ContextMenu'
import MLView from '../../components/MediaLibrary/View'
import { MLContext, UserContext } from '../../providers'
import {
  MediaLibraryActions,
  defaultInitialState as defaultMLState,
} from '../../providers/MLProvider'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'
import { encodeStringForUrl } from '../../../utils/mediaLibrary'
import { FileType, MLSortParams } from '../../constants'
import Panel from '../../components/MediaLibrary/Panel'
import PollOperations from '../../components/MediaLibrary/PollOperations'
import PollUnseenNotificationsCount from '../../components/MediaLibrary/Notifications/PollUnseenNotificationsCount'
import { RouterBackStackContext } from '../../providers/RouterBackStackProvider'
import { MouseDragSelect } from '../../components/MouseDragSelect'
import { isRestrictedUser } from '../../../utils/roles'
import { PublicLinksInfoContext } from '../../providers/PublicLinksInfoProvider'
import GenerateImageUsingAIBannerCTA from '../../components/MediaLibrary/GenerateImage/BannerCTA'
import type { RightClickContextMenuRef } from '../../components/MediaLibrary/ContextMenu/RightClickContextMenu'
import RightClickContextMenu from '../../components/MediaLibrary/ContextMenu/RightClickContextMenu'

const MLMainContentHeadingBox = styled(MainContentHeadingBox)`
  min-height: initial;
  max-height: initial;
  padding-top: 1rem;
  padding-bottom: 1rem;
`

const MediaLibraryLayout = (): React.ReactElement => {
  const routerHistory = useHistory()
  const routerLocation = useLocation()
  const backStack = useContext(RouterBackStackContext)

  const { t } = useTranslation()

  const { user } = useContext(UserContext)

  const { isPublicLink } = useContext(PublicLinksInfoContext)

  const [state, dispatch] = useContext(MLContext)

  const mouseSelectionRef = useRef<HTMLDivElement | null>(null)

  // Add ref for context menu positioning
  const contextMenuRef = useRef<RightClickContextMenuRef | null>(null)

  const onClickForMain = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
      if ((e.target as HTMLElement).tagName === 'DIV') {
        dispatch({ type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS })
      }

      contextMenuRef.current?.closeMenu()
    },
    [],
  )

  // Add right-click handler for empty areas
  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
      if ((e.target as HTMLElement).tagName === 'DIV') {
        e.preventDefault()
        e.stopPropagation()
        contextMenuRef.current?.handleContextMenu(e.nativeEvent)
      }
    },
    [],
  )

  const resetSearch = useCallback(() => {
    // if reset search is clicked with state variables set to default,
    // then useMLSearch hook won't fire request automatically
    if (
      state.semantic === '' &&
      state.semanticUrl === '' &&
      state.searchQuery === '' &&
      state.filePage === 0 &&
      state.folderPage === 0 &&
      !state.isVersionIncludedInSearch &&
      state.isFolderRestrictedSearch === 'false'
    ) {
      void state.refetch()
    }

    dispatch([
      {
        type: MediaLibraryActions.RESET_SEARCH,
      },
      {
        type: MediaLibraryActions.SET_SEARCH_SEMANTIC_VALUE,
        payload: '',
      },
      {
        type: MediaLibraryActions.SET_SEARCH_SEMANTIC_URL_VALUE,
        payload: '',
      },
      {
        type: MediaLibraryActions.SET_SORT_PARAM,
        payload: MLSortParams.CREATED,
      },
      { type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS },
      {
        type: MediaLibraryActions.SET_OPEN_ADVANCED_SEARCH,
        payload: false,
      },
      {
        type: MediaLibraryActions.SET_OPEN_SEMANTIC_IMAGE_SEARCH,
        payload: false,
      },
    ])
  }, [
    state.searchQuery,
    state.filePage,
    state.folderPage,
    state.folderPath,
    state.sort,
    state.refetch,
    state.isVersionIncludedInSearch,
    state.isFolderRestrictedSearch,
  ])

  const title = useMemo(() => {
    if (state.searchQuery) {
      return t('search_results', 'Search Results')
    }

    if (state.folderPath === '/') {
      if (isRestrictedUser(user?.roles)) {
        return t('shared_with_me', 'Shared with me')
      }
      return t('media_library', 'Media Library')
    }
    return capitalize(state.folderPath.split('/').pop())
  }, [state.folderPath, state.searchQuery, user?.roles])

  return (
    <>
      <Helmet title={title} />
      <Flex
        direction="column"
        alignItems="stretch"
        height="100%"
        overflow="auto"
        onContextMenu={handleContextMenu}
      >
        {mouseSelectionRef.current && (
          <MouseDragSelect containerRef={mouseSelectionRef} />
        )}
        <MLMainContentHeadingBox>
          <MLHeaderActionbar resetSearch={resetSearch} />
        </MLMainContentHeadingBox>
        <MLHeaderToolbar
          resetSearch={resetSearch}
          rightClickContextMenuRef={contextMenuRef}
        />
        <MainContentWrapper onClick={onClickForMain} ref={mouseSelectionRef}>
          {!isPublicLink && <GenerateImageUsingAIBannerCTA />}
          <ContextMenu />
          <RightClickContextMenu ref={contextMenuRef} />
          <MLView />
          {/* <MLView /> */}
          {/* {view === MLViewType.LIST ? <MLListView /> : null}
        {view === MLViewType.GRID ? <MLGridView /> : null} */}
        </MainContentWrapper>
        <PollOperations />
        {!isPublicLink && <PollUnseenNotificationsCount />}
      </Flex>
      <Panel />
    </>
  )
}

export default MediaLibraryLayout
